import { useCallback, useState } from 'react';
import axios from 'axios';

import { snackbar } from '@occc-app/design-system';
import { KioskImageAspectRatio } from '@occc/graphql';

import { useGenerateKioskAssetUploadSignedUrl } from './use-generate-kiosk-asset-upload-signed-url.hook';

import { ASSET_UPLOAD_ERROR_MESSAGE } from '../touchpoint.constants';

export type UploadedFileState = {
  fileName: string;
  progress: number;
  url?: string;
};

export type UseAssetUploadParams = {
  aspectRatio: KioskImageAspectRatio;
};

const uploadFileToSignedUrlWithProgress = async (
  file: File,
  signedUrl: string,
  fields: Record<string, string>,
) => {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
  formData.append('file', file);

  return axios.post(signedUrl, formData);
};

export const useAssetUpload = ({ aspectRatio }: UseAssetUploadParams) => {
  const [uploadedFileState, setUploadedFileState] = useState<UploadedFileState | null>(null);

  const [generateSignedUrl] = useGenerateKioskAssetUploadSignedUrl();

  const showErrorMessage = () => {
    snackbar.error(ASSET_UPLOAD_ERROR_MESSAGE);
  };

  const uploadFile = useCallback(
    async (selectedFile: File): Promise<string | null> => {
      setUploadedFileState({
        fileName: selectedFile.name,
        progress: 0,
        url: undefined,
      });

      try {
        const { data } = await generateSignedUrl({
          variables: {
            input: {
              fileName: selectedFile.name,
              aspectRatio,
            },
          },
        });

        if (!data?.generateKioskAssetUploadSignedUrl) {
          showErrorMessage();
          setUploadedFileState(null);
          return null;
        }

        const { signedUrl, fields, resourceUrl } = data.generateKioskAssetUploadSignedUrl;

        if (!signedUrl || !resourceUrl) {
          showErrorMessage();
          setUploadedFileState(null);
          return null;
        }

        if (Array.isArray(fields)) {
          showErrorMessage();
          setUploadedFileState(null);
          return null;
        }

        await uploadFileToSignedUrlWithProgress(
          selectedFile,
          signedUrl,
          fields as Record<string, string>,
        );

        setUploadedFileState((prev) => (prev ? { ...prev, url: resourceUrl } : null));

        return resourceUrl;
      } catch {
        showErrorMessage();
        setUploadedFileState(null);
        return null;
      }
    },
    [aspectRatio, generateSignedUrl],
  );

  const resetUploadState = useCallback(() => {
    setUploadedFileState(null);
  }, []);

  return {
    uploadedFileState,
    uploadFile,
    resetUploadState,
  };
};
